<template>
  <div class="segment-mapping-carousel">
    <!-- Upload Section -->
    <div class="upload-section">
      <label :for="`file-upload-${messageId}`" class="upload-button" :class="{ 'disabled': isUploading }" title="Upload images or videos to map to specific conversation segments for tailored content delivery. Then use the assign images to segments section to map these images to specific segments in the show">
        <div class="upload-icon">
          <svg v-if="!isUploading" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <!-- Spinner animation when uploading -->
          <div v-else class="spinner"></div>
        </div>
        <span v-if="!isUploading">Upload Media to Map to Segments</span>
        <span v-else>Uploading... {{ uploadProgress }}</span>
      </label>
      <input
        :id="`file-upload-${messageId}`"
        type="file"
        multiple
        accept="image/*,video/*,video/mp4,.mp4,.mov,video/quicktime,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        @change="handleFileUpload"
        class="file-input"
        :disabled="isUploading"
        title="Select images or videos that correspond to specific segments in your show"
      />
    </div>

    <!-- Uploads Display Carousel -->
    <div v-if="userUploads.length > 0" class="carousel-container">
      <div class="scroll-arrow left" @click.stop="scrollCarousel('left')" v-if="showScrollButtons && hasScrollLeft"
           title="Scroll to see previous segment mappings">
        <span>◀</span>
      </div>

      <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
        <div
          v-for="(upload, index) in userUploads"
          :key="upload.id"
          class="carousel-item"
          :class="{ 'active': index === activeUploadIndex }"
          @click="openPreview(upload)"
          :title="`View segment mapping: ${upload.file.name}`"
        >
          <div class="upload-card">
            <div class="upload-image">
              <!-- Handle image files -->
              <img v-if="isImageFile(upload.file.type)"
                  :src="upload.preview"
                  :alt="upload.file.name"
                  @error="handleMediaError">
              <!-- Handle video files -->
              <div v-else-if="isVideoFile(upload.file.type)" class="video-thumbnail">
                <div class="video-icon">▶</div>
                <img v-if="upload.preview"
                     :src="upload.preview"
                     :alt="upload.file.name"
                     @error="handleMediaError">
                <div v-else class="video-placeholder">
                  MP4
                </div>
              </div>
              <!-- Handle other file types -->
              <div v-else class="file-icon" :class="getFileIconClass(upload.file.type)">
                {{ getFileExtension(upload.file.name).toUpperCase() }}
              </div>
            </div>
            <div class="upload-info">
              <div class="upload-title">{{ truncateFileName(upload.file.name) }}</div>
              <div class="upload-meta">
                {{ formatFileSize(upload.file.size) }} · {{ formatFileType(upload.file.type) }}
              </div>
            </div>
            <button class="remove-upload" @click.stop="removeUpload(index)" title="Remove this segment mapping">×</button>
          </div>
        </div>
      </div>

      <div class="scroll-arrow right" @click.stop="scrollCarousel('right')" v-if="showScrollButtons && hasScrollRight"
           title="Scroll to see more segment mappings">
        <span>▶</span>
      </div>
    </div>

    <!-- Preview Modal -->
    <div v-if="previewOpen" class="preview-modal" @click="closePreview">
      <div class="preview-content" @click.stop>
        <div class="preview-header">
          <h3>{{ activeUpload ? activeUpload.file.name : '' }}</h3>
          <button class="close-preview" @click="closePreview" title="Close segment mapping preview">×</button>
        </div>
        <div class="preview-body">
          <!-- Image preview -->
          <img v-if="activeUpload && isImageFile(activeUpload.file.type)"
               :src="activeUpload.preview"
               :alt="activeUpload.file.name">

          <!-- Video preview -->
          <video v-else-if="activeUpload && isVideoFile(activeUpload.file.type)"
                 controls
                 autoplay
                 muted
                 preload="metadata"
                 loop
                 class="preview-video"
                 :src="activeUpload.dataUrl"
                 :alt="activeUpload.file.name"
                 @error="handleMediaError"></video>

          <!-- Other file types preview -->
          <div v-else class="preview-placeholder">
            <div class="file-icon large" :class="getFileIconClass(activeUpload ? activeUpload.file.type : '')">
              {{ getFileExtension(activeUpload ? activeUpload.file.name : '').toUpperCase() }}
            </div>
            <p>Preview not available</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress Overlay -->
    <div v-if="isUploading" class="upload-overlay">
      <div class="upload-progress-container">
        <div class="upload-progress-spinner"></div>
        <div class="upload-progress-text">
          <div class="upload-file-name">{{ currentUploadingFile }}</div>
          <div class="upload-progress-status">{{ uploadProgressMessage }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/api';
import { mapActions } from 'vuex';
export default {
  name: 'UserUploadsCarousel',
  props: {
    messageId: {
      required: true
    },
    isPro: {
      type: Boolean,
      default: false
    },
    showUploadSection: {
      type: Boolean,
      default: false
    },
    isExpandedView: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      userUploads: [],
      showScrollButtons: true,
      hasScrollLeft: false,
      hasScrollRight: true,
      activeUploadIndex: -1,
      previewOpen: false,
      activeUpload: null,
      uploadCounter: 0,
      isUploading: false,
      uploadProgress: '0%',
      currentUploadingFile: '',
      totalFiles: 0,
      completedFiles: 0,
      uploadProgressMessage: ''
    }
  },
  computed: {
    documentIdKey() {
      return `uploads-${this.messageId}`;
    },
    // Unique identifiers for each upload, based on messageId
    uploadKeys() {
      return this.userUploads.map(upload => `${this.messageId}_${upload.id}`);
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),
    async handleFileUpload(event) {
      // Check if the user is allowed to upload files
      if (!this.isPro) {
        this.showTempMessageFn({
          message: 'Customization is only available for Pro users.',
          type: 'warning'
        });
        return;
      }
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Initialize upload tracking variables
      this.isUploading = true;
      this.totalFiles = files.length;
      this.completedFiles = 0;
      this.uploadProgress = '0%';
      this.uploadProgressMessage = `Preparing to upload ${this.totalFiles} file(s)...`;

      // Process each file sequentially to avoid overwhelming the server
      for (const [index, file] of Array.from(files).entries()) {
        // Set current file info
        this.currentUploadingFile = file.name;
        this.uploadProgressMessage = `Uploading file ${index + 1} of ${this.totalFiles}`;

        // Check if file size is less than 50MB
        if (file.size > 50 * 1024 * 1024) {
          this.$emit('upload-error', 'File size exceeds 50MB limit');
          this.showTempMessageFn({
            message: `File size exceeds 50MB limit for ${file.name}`,
            type: 'error',
            timeout: 5000
          });
          continue;
        }

        try {
          // Create form data for this file
          const formData = new FormData();
          formData.append('file', file);

          // Upload to backend API with progress tracking
          const response = await API.post(`/user_customization/messages/${this.messageId}/documents`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              // Calculate and update the progress percentage for this file
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.uploadProgress = `${percentCompleted}%`;
            }
          });

          // Parse the response data
          const documentData = response.data;

          // Create a local representation of the document
          const upload = {
            id: documentData.id,
            file: {
              name: documentData.filename,
              type: documentData.type,
              size: documentData.size
            },
            preview: documentData.thumbnailUrl || documentData.url,
            timestamp: new Date().toISOString(),
            uniqueKey: documentData.uniqueKey,
            dataUrl: documentData.url
          };

          // Add to local collection
          this.userUploads.push(upload);

          // Increment completed files counter
          this.completedFiles++;
          this.uploadProgressMessage = `Completed ${this.completedFiles} of ${this.totalFiles} files`;

          // Emit event with upload data
          this.$emit('upload-added', {
            messageId: this.messageId,
            upload: upload,
            uniqueKey: documentData.uniqueKey,
            dataUrl: documentData.url
          });

        } catch (error) {
          console.error('Error uploading document:', error);
          this.showTempMessageFn({
            message: `Failed to upload ${file.name}. Are you the owner of this show?`,
            type: 'error',
            timeout: 5000
          });
          this.$emit('upload-error', `Failed to upload ${file.name}: ${error.message}`);
        }
      }

      // Reset upload status after all files are processed
      this.isUploading = false;
      this.currentUploadingFile = '';
      this.uploadProgress = '0%';
      this.uploadProgressMessage = '';

      // Show a success message after all uploads complete
      this.showTempMessageFn({
        message: `Successfully uploaded ${this.completedFiles} file(s).`,
        type: 'success',
        timeout: 3000
      });

      // Clear input value to allow uploading the same file again
      event.target.value = '';
    },

    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';
      return 'generic-icon';
    },

    formatFileSize(size) {
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
      return Math.round(size / (1024 * 1024) * 10) / 10 + ' MB';
    },

    formatFileType(type) {
      if (!type) return 'UNKNOWN';
      const fileType = type.split('/').pop().toUpperCase();
      return fileType === 'MP4' ? 'VIDEO' : fileType;
    },

    truncateFileName(name) {
      return name.length > 20 ? name.substring(0, 17) + '...' : name;
    },

    // Remove a document
    async removeUpload(index) {
      const removedUpload = this.userUploads[index];
      if (!removedUpload) return;

      try {
        // Call backend API to delete the document
        await API.delete(`/user_customization/messages/${this.messageId}/documents/${removedUpload.id}`);

        // Remove from local collection
        this.userUploads.splice(index, 1);

        // Emit event
        this.$emit('upload-removed', {
          messageId: this.messageId,
          uploadId: removedUpload.id,
          uniqueKey: removedUpload.uniqueKey
        });
      } catch (error) {
        console.error('Error removing document:', error);
        // You might want to show an error message to the user
      }
    },

    // Load uploads from backend instead of localStorage
    async loadUploadsFromStorage() {
      try {
        // Fetch documents from backend API
        const token = localStorage.getItem('token');
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token ? `/user_customization/messages/${this.messageId}/documents` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/documents`;
        const response = await API.get(url);
        const documentsData = response.data;

        // Reset local collection
        this.userUploads = [];

        // Process documents from the response
        if (documentsData && documentsData[this.messageId]) {
          Object.values(documentsData[this.messageId]).forEach(doc => {
            this.userUploads.push({
              id: doc.id,
              file: {
                name: doc.name,
                type: doc.type,
                size: doc.size
              },
              preview: doc.preview || doc.dataUrl,
              timestamp: doc.timestamp,
              uniqueKey: doc.uniqueKey,
              dataUrl: doc.dataUrl
            });
          });
        }

        this.$emit('uploads-loaded', {
          messageId: this.messageId,
          uploadsCount: this.userUploads.length,
          uploads: this.userUploads
        });

      } catch (error) {
        console.error('Error loading documents:', error);
      }
    },

    // No need to save to localStorage anymore
    saveUploadsToStorage() {
      // This method is no longer needed since data is stored in the backend
    },

    scrollCarousel(direction) {
      if (!this.$refs.carouselTrack) return;

      const scrollAmount = 300;
      const currentScroll = this.$refs.carouselTrack.scrollLeft;

      if (direction === 'left') {
        this.$refs.carouselTrack.scrollLeft = currentScroll - scrollAmount;
      } else {
        this.$refs.carouselTrack.scrollLeft = currentScroll + scrollAmount;
      }
    },

    handleScroll() {
      if (!this.$refs.carouselTrack) return;

      const track = this.$refs.carouselTrack;

      // Check if we can scroll left or right
      this.hasScrollLeft = track.scrollLeft > 0;
      this.hasScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth);
    },

    handleMediaError(e) {
      e.target.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      // Add a message about the failed load
      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    openPreview(upload) {
      this.activeUpload = upload;
      this.previewOpen = true;

      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
    },

    closePreview() {
      this.previewOpen = false;
      this.activeUpload = null;

      // Restore body scrolling
      document.body.style.overflow = '';
    },
  },
  mounted() {
    this.$nextTick(() => {
      // check if messageId is available and is an integer
      if (this.messageId && !isNaN(this.messageId)) {
        this.loadUploadsFromStorage();
      }

      if (this.$refs.carouselTrack) {
        this.handleScroll();
      }

      // Find the message container for this component
      const messageContainer = document.querySelector(`[data-message-id="${this.messageId}"]`);
      if (messageContainer) {
        // You can perform any message-specific initialization here
      }
    });
  }
}
</script>

<style scoped>
.segment-mapping-carousel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 10px;
  border-radius: 8px;
  margin: 0 auto;
  max-width: 100%;
  overflow: hidden;
  position: relative;
}

/* Upload Section */
.upload-section {
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
}

/* Enhanced Upload Button Styling */
.upload-button {
  display: flex;
  align-items: center;
  gap: 12px; /* Increased from 8px */
  padding: 12px 20px; /* Increased from 8px 16px */
  border: 2px dashed #e53e3e; /* Changed from 1px to 2px */
  border-radius: 8px;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.3s ease; /* Changed from 0.2s */
  background-color: rgba(229, 62, 62, 0.05); /* Added subtle background */
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.1); /* Added subtle shadow */
}

.upload-button:hover:not(.disabled) {
  border-color: #e53e3e;
  background-color: rgba(229, 62, 62, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(229, 62, 62, 0.2);
}

.upload-button.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  border-style: solid;
  transform: none;
  box-shadow: none;
}

/* Enhanced Upload Text Styling */
.upload-button span {
  font-weight: 600; /* Changed from normal */
  font-size: 1.05rem; /* Increased from default */
  letter-spacing: 0.02em; /* Added letter spacing */
  background: linear-gradient(90deg, #e53e3e, #ff0066);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent; /* Make text transparent to show gradient */
  text-shadow: 0 1px 2px rgba(229, 62, 62, 0.2); /* Added subtle text shadow */
  position: relative;
}

/* Add pulsing animation to the text */
.upload-button:not(.disabled) span::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #e53e3e, #ff0066);
  opacity: 0.7;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.5;
    transform: scaleX(0.9);
  }
  50% {
    opacity: 1;
    transform: scaleX(1);
  }
}

/* Enhanced Upload Icon */
.upload-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.upload-button:hover:not(.disabled) .upload-icon {
  transform: translateY(-3px);
}

.upload-icon svg {
  stroke: #e53e3e;
  stroke-width: 2.5px; /* Made icon stroke slightly thicker */
  width: 28px; /* Increased from 24px */
  height: 28px; /* Increased from 24px */
  filter: drop-shadow(0 2px 3px rgba(229, 62, 62, 0.3));
}

/* Spinner Animation */
.spinner {
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 3px solid #e53e3e;
  width: 28px;
  height: 28px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Mobile Responsive Adjustments */
@media screen and (max-width: 768px) {
  .upload-button {
    padding: 10px 16px;
  }

  .upload-button span {
    font-size: 0.95rem;
  }

  .upload-icon svg {
    width: 24px;
    height: 24px;
  }
}

.file-input {
  display: none;
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
}

.carousel-track {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0.5rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  gap: 16px;
}

.carousel-track::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.carousel-item {
  flex: 0 0 auto;
  width: 180px;
  height: 170px;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.carousel-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00ff00 0%, #ff00ff 33%, #00ffff 66%, #ff00ff 100%);
  background-size: 200% 100%;
  animation: gradientSlide 1.5s linear infinite;
}

.upload-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(4, 255, 134, 0.07);
  border: 1px solid rgba(4, 255, 134, 0.15);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
}

.upload-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  border-color: rgba(4, 255, 134, 0.3);
  background: rgba(4, 255, 134, 0.12);
}

.upload-image {
  height: 65%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.03);
}

.upload-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.upload-card:hover .upload-image img {
  transform: scale(1.05);
}

/* Video specific styles */
.video-thumbnail {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-icon {
  position: absolute;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(4, 255, 134, 0.7);
  color: white;
  border-radius: 50%;
  font-size: 1.2rem;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  color: white;
  font-size: 1rem;
  font-weight: bold;
}

.preview-video {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 4px;
}

.upload-info {
  padding: 10px;
  height: 35%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.upload-title {
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 4px;
}

.upload-meta {
  font-size: 0.7rem;
  color: gray;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-upload {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  font-size: 16px;
  font-weight: bold;
  color: #ff0066;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.upload-card:hover .remove-upload {
  opacity: 1;
}

/* File Icons */
.file-icon {
  width: 80px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  color: white;
  position: relative;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.file-icon.large {
  width: 120px;
  height: 150px;
  font-size: 2rem;
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; } /* Pink color for videos */
.generic-icon:before { background: #9E9E9E; }

/* Scroll Arrows */
.scroll-arrow {
  position: absolute;
  width: 40px;
  height: 40px;
  background: rgba(4, 255, 134, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
}

.scroll-arrow:hover {
  background: rgba(4, 255, 134, 0.3);
}

.scroll-arrow.left {
  left: 10px;
}

.scroll-arrow.right {
  right: 10px;
}

.scroll-arrow span {
  color: white;
  font-size: 0.9rem;
}

/* Preview Modal */
.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.preview-content {
  width: 90%;
  max-width: 900px;
  max-height: 80vh;
  background: #222;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(4, 255, 134, 0.1);
  border-bottom: 1px solid rgba(4, 255, 134, 0.2);
}

.preview-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: white;
  font-weight: 500;
}

.close-preview {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
}

.preview-body {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.preview-body img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
}

.preview-placeholder p {
  color: #aaa;
  font-size: 1.1rem;
}

.media-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

/* Upload Overlay */
.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;
  backdrop-filter: blur(2px);
}

.upload-progress-container {
  background: #222;
  padding: 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(4, 255, 134, 0.2);
  max-width: 90%;
}

.upload-progress-spinner {
  border: 4px solid rgba(4, 255, 134, 0.1);
  border-radius: 50%;
  border-top: 4px solid rgba(4, 255, 134, 0.8);
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

.upload-progress-text {
  flex: 1;
  min-width: 0;
}

.upload-file-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: white;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-progress-status {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Animations */
@keyframes gradientSlide {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Mobile Responsive Design */
@media screen and (max-width: 768px) {
  .carousel-item {
    width: 160px;
    height: 140px;
  }

  .scroll-arrow {
    width: 32px;
    height: 32px;
  }

  .upload-title {
    font-size: 0.8rem;
  }

  .preview-content {
    width: 95%;
  }

  .upload-progress-container {
    padding: 15px;
    gap: 12px;
  }

  .upload-progress-spinner {
    width: 30px;
    height: 30px;
  }

  .upload-file-name {
    font-size: 0.8rem;
  }
}
</style>
